/**
 * @file useTemplateGuards.ts
 * @description Замки шаблона страницы товара поверх действий с блоками
 * (phase19.16 Р2а; uiux-design-r2a §3, §6; SB34; db-design-r2a, ответ 2а).
 *
 * На странице-шаблоне (`kind = TEMPLATE`) у блока с обязательными
 * элементами (название, цена и покупка, сведения, продавец):
 *  - «Скрыть» не выполняется — объяснение «скрыть его нельзя»;
 *  - «Удалить» — окно «Перенести и удалить блок»: обязательные элементы
 *    переносятся в блок с «Ценой и покупкой» (иначе в первый другой) и блок
 *    удаляется одной транзакцией (`DELETE blocks/:id?moveRequiredTo=`, §19.3).
 * На обычных страницах действия не меняются. Сервер проверяет то же самое
 * (`checkProductTemplate` в транзакции) — здесь объяснение до ошибки.
 */

import { useCallback, useMemo } from "react";

import type { BlockActions } from "./useBlockActions";
import type { BuilderPagesApi } from "./useBuilderPages";
import type { UndoApi } from "./useUndoStack";
import { blockTitle } from "../../components/site-pages/block-meta";
import type { ConfirmRequest } from "../../components/ui/use-confirm";
import { sitePages, type SitePageBlock } from "../../lib/api-site-pages";

import { blockErrorText } from "../../lib/site-builder/block-errors";
import { REQUIRED_BLOCK_HIDE_TEXT } from "../../lib/site-builder/product-element-meta";
import { blockHasRequired, removeBlockText, removePlan } from "../../lib/site-builder/template-rules";

interface Deps {
  pages: BuilderPagesApi;
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  notify: { error: (text: string) => void; success: (text: string) => void };
  onBlockGone: (id: string) => void;
  /** История «Отменить»: после переноса обязательных её записи устарели. */
  undo: Pick<UndoApi, "clear">;
}

/** Нельзя удалить: в шаблоне нет другого блока, куда перенести обязательные. */
export const LAST_REQUIRED_BLOCK_TEXT = "В шаблоне нет другого блока — обязательные элементы некуда перенести. Сначала добавьте блок.";

export function useTemplateGuards(actions: BlockActions, { pages, confirm, notify, onBlockGone, undo }: Deps): BlockActions {
  const { page, reloadPage, bumpCanvas } = pages;
  const isTemplate = page?.kind === "TEMPLATE";

  const toggle = useCallback(
    async (block: SitePageBlock, isEnabled: boolean) => {
      if (isTemplate && !isEnabled && blockHasRequired(block)) {
        notify.error(REQUIRED_BLOCK_HIDE_TEXT);
        return;
      }
      await actions.toggle(block, isEnabled);
    },
    [actions, isTemplate, notify],
  );

  const remove = useCallback(
    async (block: SitePageBlock) => {
      if (!isTemplate || !page || !blockHasRequired(block)) {
        await actions.remove(block);
        return;
      }
      const plan = removePlan(page.blocks, block);
      if (!plan.target) {
        notify.error(LAST_REQUIRED_BLOCK_TEXT);
        return;
      }
      const ok = await confirm({ title: `Удалить блок «${blockTitle(block)}»?`, description: removeBlockText(plan), action: "Перенести и удалить блок", danger: true });
      if (!ok) return;
      try {
        await sitePages.removeBlock(block.id, { moveRequiredTo: plan.target.id });
        // Перенос не записывается в «Отменить» — прежние записи вернули бы элементы вторыми.
        undo.clear();
        onBlockGone(block.id);
        notify.success(`Блок удалён, обязательные элементы — в блоке «${blockTitle(plan.target)}»`);
      } catch (err) {
        notify.error(blockErrorText(err, "Не удалось перенести элементы и удалить блок."));
      } finally {
        await reloadPage();
        bumpCanvas();
      }
    },
    [actions, bumpCanvas, confirm, isTemplate, notify, onBlockGone, page, reloadPage, undo],
  );

  return useMemo(() => (isTemplate ? { ...actions, toggle, remove } : actions), [actions, isTemplate, remove, toggle]);
}
